import { useMemo, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { Account, Address } from 'shared/types/account';
import useSWR, { mutate } from 'swr';
import { deleteCognitoCookies, getCognitoCookies, getLocalUserData, getUserData } from 'helpers/padi/cognito';
import { GetSloSession } from 'helpers/padi/slo';
import { getCookie } from 'helpers/utils/cookies';
import { getLocalStorage } from 'helpers/utils/localStorage';
import { sdk } from 'sdk';
import { revalidateOptions } from 'frontastic';
import { GetAccountResult, RegisterAccount, UpdateAccount, UseAccountReturn } from './types';
import { createContext, useContext } from 'react';

export interface SSOToken {
  aud: string;
  exp: number;
}

const projectConfigCache = new Map<string, any>();
const pendingRequests = new Map<string, Promise<any>>();

const useAccount = (): UseAccountReturn => {
  const extensions = sdk.composableCommerce;

  const result = useSWR('/action/account/getAccount', extensions.account.getAccount, revalidateOptions);

  const data = useMemo(() => {
    if (result.data?.isError) return { loggedIn: false, accountLoading: false, error: result.error };

    const account = (result.data?.data as GetAccountResult)?.account as Account;

    if (account?.accountId) return { account, loggedIn: true, accountLoading: false };

    return {
      loggedIn: false,
      account: undefined,
      accountLoading: true,
      error: result.error,
    };
  }, [result]);

  const shippingAddresses = useMemo(() => {
    if (!data.account) return [];

    return (data.account.addresses ?? []).filter((address) => address.isShippingAddress);
  }, [data.account]);

  const billingAddresses = useMemo(() => {
    if (!data.account) return [];

    return (data.account.addresses ?? []).filter((address) => address.isBillingAddress);
  }, [data.account]);

  const defaultShippingAddress = useMemo(() => {
    return data.account?.addresses?.find((address) => address.isDefaultShippingAddress);
  }, [data.account]);

  const defaultBillingAddress = useMemo(() => {
    return data.account?.addresses?.find((address) => address.isDefaultBillingAddress);
  }, [data.account]);

  const login = async (email: string, password: string, remember?: boolean): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
      password,
      remember,
    };

    const res = await extensions.account.login(payload);

    mutate('/action/account/getAccount');
    mutate('/action/cart/getCart');
    mutate('/action/wishlist/getWishlist');

    return res.isError ? ({} as Account) : res.data;
  };

  const logout = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    await extensions.account.logout();

    mutate('/action/account/getAccount');
    mutate('/action/cart/getCart');
    mutate('/action/wishlist/getWishlist');
  }, []);

  const register = useCallback(async (account: RegisterAccount): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.register(account);

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const confirm = useCallback(async (token: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.confirm({ token });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const requestConfirmationEmail = useCallback(async (email: string, password: string): Promise<void> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
      password,
    };

    await extensions.account.requestConfirmationEmail(payload);
  }, []);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.changePassword({ oldPassword, newPassword });

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const requestPasswordReset = useCallback(async (email: string): Promise<void> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
    };

    await extensions.account.requestResetPassword(payload);
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.resetPassword({ token, newPassword });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const update = useCallback(async (account: UpdateAccount): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateAccount(account);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.addAddress(address);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addShippingAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !response.data.loggedIn) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addShippingAddress',
      payload: { account: response.data.account, address },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addBillingAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !response.data.loggedIn) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addBillingAddress',
      payload: { account: response.data.account, address },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const updateAddress = useCallback(async (address: Address): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateAddress(address);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const removeAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.removeAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const setDefaultBillingAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.setDefaultBillingAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const setDefaultShippingAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.setDefaultShippingAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const getAccountForSso = useCallback(async (idToken: string, storeNumber: string, impactId: string) => {
    const payload = {
      idToken,
      storeNumber,
      impactId,
    };
    const res = await sdk.callAction({ actionName: 'account/getAccountForSso', payload });

    mutate('/action/account/getAccount');
    mutate('/action/cart/getCart');
    mutate('/action/wishlist/getWishlist');

    return (res.isError ? {} : res.data) as Account;
  }, []);

  const updateCustomerGroup = useCallback(async (sub: string): Promise<Account> => {
    const payload = {
      sub,
    };
    const res = await sdk.callAction({ actionName: 'account/updateCustomerGroup', payload });

    return (res.isError ? {} : res.data) as Account;
  }, []);

  const updateAuthMode = useCallback(async (): Promise<Account> => {
    const res = await sdk.callAction({ actionName: 'account/updateAuthMode' });

    return (res.isError ? {} : res.data) as Account;
  }, []);

  const getProjectConfig = useCallback(
    async (key: string): Promise<any> => {
      if (projectConfigCache.has(key)) {
        return projectConfigCache.get(key);
      }

      if (pendingRequests.has(key)) {
        return pendingRequests.get(key);
      }

      const request = (async () => {
        try {
          const payload = { key };
          const res = await sdk.callAction({ actionName: 'account/getProjectConfig', payload });
          const result = res.isError ? {} : res.data;

          projectConfigCache.set(key, result);

          return result as any;
        } finally {
          pendingRequests.delete(key);
        }
      })();

      pendingRequests.set(key, request);

      return request;
    },
    [],
  );

  const updateAccountSession = useCallback(async (): Promise<Account> => {
    const res = await sdk.callAction({ actionName: 'account/updateAccountSession' });

    return (res.isError ? {} : res.data) as any;
  }, []);

  const refreshSession = useCallback(async (): Promise<Account> => {
    const res = await sdk.callAction({ actionName: 'account/refreshSession' });

    return (res.isError ? {} : res.data) as any;
  }, []);

  const verifyLoggedIn = useCallback(async (): Promise<boolean> => {
    const userData = getUserData();

    if (!data.loggedIn && !userData) return false;

    // Check SLO session.
    const sloSession = await GetSloSession(userData?.sub);
    if (!sloSession || sloSession?.logout == true) {
      logout()
        .then(() => deleteCognitoCookies())
        .then(() => {
          Object.keys(localStorage)
            .filter((key) => !key.startsWith('padi'))
            .map((key) => localStorage.removeItem(key));
        });
      return false;
    }

    if (data.loggedIn && sloSession) return true;

    let idToken = null;
    let refreshToken = null;
    const cognitoCookies = document ? getCognitoCookies() : null;
    if (cognitoCookies) {
      for (const [key, value] of Object.entries(cognitoCookies)) {
        if (key.endsWith('idToken')) {
          idToken = value;
        } else if (key.endsWith('refreshToken')) {
          refreshToken = value;
        }
      }
    }
    if (!idToken) return false;
    const clientId = await getProjectConfig('EXTENSION_COMMERCETOOLS_COGNITO_CLIENT_ID');
    const user = jwtDecode<SSOToken>(idToken);
    const { aud, exp } = user;
    const current = Number(new Date());
    // refresh token
    if (refreshToken && (aud !== clientId.setting || exp < current / 1000)) {
      const payload = {
        key: 'EXTENSION_COMMERCETOOLS_COGNITO_AUTH_URL',
      };
      const res = await sdk.callAction({ actionName: 'account/getProjectConfig', payload });
      if (!res.isError) {
        const response = await fetch(`${(res.data as any).setting}/oauth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            clientId: clientId.setting,
            idToken,
            refreshToken,
          }),
        });
        if (!response.ok) return false;
        const data = await response.json();
        idToken = data.idToken;
        refreshToken = data.refreshToken;
      }
    }
    window.localStorage.setItem('idToken', idToken);
    window.localStorage.setItem('refreshToken', refreshToken);
    // get Account
    const diveShopLocalStorage = getLocalStorage('affiliateDiveShop');
    const impactIdCookie = getCookie('PADI.Impact.irclickid');
    await getAccountForSso(idToken, diveShopLocalStorage || '', impactIdCookie || '');
    return true;
  }, [data.loggedIn, getAccountForSso, getProjectConfig, logout]);

  return {
    ...data,
    shippingAddresses,
    billingAddresses,
    defaultShippingAddress,
    defaultBillingAddress,
    getAccountForSso,
    updateCustomerGroup,
    updateAuthMode,
    getProjectConfig,
    login,
    logout,
    register,
    confirm,
    requestConfirmationEmail,
    changePassword,
    requestPasswordReset,
    resetPassword,
    update,
    addAddress,
    addBillingAddress,
    addShippingAddress,
    updateAddress,
    removeAddress,
    setDefaultBillingAddress,
    setDefaultShippingAddress,
    updateAccountSession,
    refreshSession,
    verifyLoggedIn,
  };
};

export default useAccount;